import React from 'react';
import { Link } from 'react-router-dom';
import { Badge, ErrorState, SectionCard } from '@parsina/framework';
import { ChevronLeft, Link2 } from 'lucide-react';

import { useTranslation, MIRROR_ICON } from '../../i18n';
import { errorMessage, gql } from '../../lib/api';
import { num } from '../../lib/aiPricingView';
import { SERVICE_DETAIL_QUERY } from './queries';
import ModelShareChips from './ModelShareChips';
import {
  BlockSkeleton, Code, MarginBadge, ParsehCell, UsdCell, useEnumLabel, useServiceLabel,
} from './shared';

/**
 * The brief's arrow diagram, drawn literally, once — for the most expensive
 * service of the window:
 *
 *   عامل → مدل‌ها → میانگین توکن → هزینهٔ دلاری → هزینهٔ تمام‌شده → سیاست → قیمت پارسه
 *
 * Every link is a fact from `aiServiceDetail`, and the whole card is a door
 * into the service drawer. The arrow glyph mirrors with the reading direction
 * (`MIRROR_ICON`) — a chain that reads backwards in English would say the
 * price causes the agent.
 */
export default function ChainStepper({ serviceKey, window, href, 'data-testid': testId = 'ai-overview-chain' }) {
  const { t, formatNumber } = useTranslation();
  const label = useServiceLabel();
  const basisLabel = useEnumLabel('aiPricing.services.drawer.basisSource');

  const [detail, setDetail] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    setError(null);
    gql(SERVICE_DETAIL_QUERY, { serviceKey, window })
      .then((d) => setDetail(d?.aiServiceDetail || null))
      .catch((e) => setError(errorMessage(e, t)))
      .finally(() => setLoading(false));
  }, [serviceKey, window, t]);

  React.useEffect(() => { load(); }, [load]);

  const row = detail?.row;
  const pricing = detail?.pricing;

  const steps = row ? [
    {
      key: 'agent',
      label: t('aiPricing.overview.chain.agent'),
      value: (
        <span className="flex flex-col">
          <span className="font-semibold">{label(row)}</span>
          <Code>{row.serviceKey}</Code>
        </span>
      ),
    },
    {
      key: 'models',
      label: t('aiPricing.overview.chain.models'),
      value: <ModelShareChips models={detail.models?.length ? detail.models : row.models} />,
    },
    {
      key: 'tokens',
      label: t('aiPricing.overview.chain.tokens'),
      value: (
        <span className="tabular-nums">
          {t('aiPricing.overview.chain.tokensValue', {
            input: num(row.avgInputTokens) == null ? t('common.value.none') : formatNumber(num(row.avgInputTokens), { maximumFractionDigits: 0 }),
            output: num(row.avgOutputTokens) == null ? t('common.value.none') : formatNumber(num(row.avgOutputTokens), { maximumFractionDigits: 0 }),
          })}
        </span>
      ),
    },
    {
      key: 'usd',
      label: t('aiPricing.overview.chain.usd'),
      value: <span><UsdCell value={row.meanCostUsd} /> {t('common.units.usd')}</span>,
    },
    {
      key: 'loaded',
      label: t('aiPricing.overview.chain.loaded'),
      value: (
        <span className="inline-flex items-center gap-1">
          <UsdCell value={pricing?.basisCostUsd} /> {t('common.units.usd')}
          <Badge variant="outline">{basisLabel(pricing?.basisSource)}</Badge>
        </span>
      ),
    },
    {
      key: 'policy',
      label: t('aiPricing.overview.chain.policy'),
      value: pricing?.policyName || t('aiPricing.overview.chain.codeDefault'),
    },
    {
      key: 'price',
      label: t('aiPricing.overview.chain.price'),
      value: (
        <span className="inline-flex items-center gap-1.5">
          <ParsehCell value={row.effectiveParseh} suffix={row.unit === 'minute' ? t('aiPricing.units.perMinute') : ''} />
          {t('common.units.parseh')}
          <MarginBadge value={row.marginPct} />
        </span>
      ),
    },
  ] : [];

  return (
    <SectionCard
      title={t('aiPricing.overview.chain.title')}
      sub={t('aiPricing.overview.chain.sub')}
      icon={Link2}
      data-testid={testId}
    >
      {loading ? (
        <div data-state="loading" role="status" aria-busy="true" aria-label={t('aiPricing.overview.chain.loading')}>
          <BlockSkeleton lines={2} height={40} />
        </div>
      ) : error ? (
        <ErrorState
          title={t('aiPricing.overview.chain.error')}
          description={error}
          onRetry={load}
          retryLabel={t('common.actions.retry')}
          data-testid={`${testId}-error`}
        />
      ) : (
        <Link
          to={href}
          className="block rounded-lg outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`${t('aiPricing.overview.chain.open')}: ${label(row)}`}
          data-testid={`${testId}-link`}
        >
          <ol className="flex flex-wrap items-stretch gap-2 p-1">
            {steps.map((step, i) => (
              <li key={step.key} className="flex items-center gap-2">
                <div className="min-w-[8rem] rounded-lg border border-border bg-card px-3 py-2">
                  <div className="text-[11px] text-muted-foreground">{step.label}</div>
                  <div className="mt-1 text-sm text-foreground">{step.value}</div>
                </div>
                {i < steps.length - 1 ? (
                  <ChevronLeft size={16} aria-hidden style={MIRROR_ICON} className="shrink-0 text-muted-foreground" />
                ) : null}
              </li>
            ))}
          </ol>
        </Link>
      )}
    </SectionCard>
  );
}
